/**
 * Pure Pricing Engine
 * 
 * This is a pure function with NO dependencies on:
 * - React
 * - Supabase
 * - UI components
 * - External services
 * 
 * It only performs mathematical calculations based on input parameters.
 */

/**
 * Calculate final booking price
 * 
 * @param {Object} params - Pricing parameters
 * @param {number} params.baseHourPrice - Base price per hour
 * @param {number} params.hours - Number of hours
 * @param {number} params.hoursMultiplier - Hours multiplier (default: 1.0)
 * @param {number} params.cityMultiplier - City multiplier (default: 1.0)
 * @param {number} params.experienceMultiplier - Experience level multiplier (default: 1.0)
 * @param {number} params.equipmentMultiplier - Combined equipment multiplier (default: 1.0)
 * @param {number} params.typeMultiplier - Photography type multiplier (default: 1.0)
 * @param {number} params.shootingMethodMultiplier - Shooting method multiplier (default: 1.0)
 * @param {number} params.placeTypeMultiplier - Shooting place type multiplier (default: 1.0)
 * @param {number} params.workloadMultiplier - Workload multiplier (default: 1.0)
 * @param {number} params.vatPercent - VAT percentage (default: 15)
 * @param {boolean} params.isEnabled - Whether smart pricing is enabled (default: true)
 * 
 * @returns {Object} Price breakdown
 * @returns {number} returns.baseHourPrice - Base price per hour
 * @returns {number} returns.hours - Number of hours
 * @returns {number} returns.subtotal - Subtotal before VAT
 * @returns {number} returns.vatAmount - VAT amount
 * @returns {number} returns.total - Final total price
 * @returns {number} returns.vatPercent - VAT percentage used
 * @returns {Object} returns.multipliers - Applied multipliers
 * @returns {Array} returns.breakdown - Price breakdown array
 */
export function calculatePrice({
  baseHourPrice,
  hours = 1,
  hoursMultiplier = 1.0,
  cityMultiplier = 1.0,
  experienceMultiplier = 1.0,
  equipmentMultiplier = 1.0,
  typeMultiplier = 1.0,
  shootingMethodMultiplier = 1.0,
  placeTypeMultiplier = 1.0,
  workloadMultiplier = 1.0,
  vatPercent = 15,
  isEnabled = true
}) {
  // Validate inputs
  if (typeof baseHourPrice !== 'number' || baseHourPrice <= 0) {
    throw new Error('baseHourPrice must be a positive number');
  }
  if (typeof hours !== 'number' || hours <= 0) {
    throw new Error('hours must be a positive number');
  }

  // Ensure multipliers are valid numbers (default to 1.0 if invalid)
  const safeHoursMultiplier = typeof hoursMultiplier === 'number' && hoursMultiplier > 0 ? hoursMultiplier : 1.0;
  const safeCityMultiplier = typeof cityMultiplier === 'number' && cityMultiplier > 0 ? cityMultiplier : 1.0;
  const safeExperienceMultiplier = typeof experienceMultiplier === 'number' && experienceMultiplier > 0 ? experienceMultiplier : 1.0;
  const safeEquipmentMultiplier = typeof equipmentMultiplier === 'number' && equipmentMultiplier > 0 ? equipmentMultiplier : 1.0;
  const safeTypeMultiplier = typeof typeMultiplier === 'number' && typeMultiplier > 0 ? typeMultiplier : 1.0;
  const safeShootingMethodMultiplier = typeof shootingMethodMultiplier === 'number' && shootingMethodMultiplier > 0 ? shootingMethodMultiplier : 1.0;
  const safePlaceTypeMultiplier = typeof placeTypeMultiplier === 'number' && placeTypeMultiplier > 0 ? placeTypeMultiplier : 1.0;
  const safeWorkloadMultiplier = typeof workloadMultiplier === 'number' && workloadMultiplier > 0 ? workloadMultiplier : 1.0;
  const safeVatPercent = typeof vatPercent === 'number' && vatPercent >= 0 ? vatPercent : 15;

  // If smart pricing is disabled, use simple calculation
  if (!isEnabled) {
    const basePrice = baseHourPrice * hours;
    const subtotal = basePrice * safeHoursMultiplier;
    const vatAmount = (subtotal * safeVatPercent) / 100;
    const totalBeforeWorkload = subtotal + vatAmount;
    // Apply workload_multiplier at final stage
    const total = totalBeforeWorkload * safeWorkloadMultiplier;

    return {
      baseHourPrice,
      hours,
      subtotal,
      vatAmount,
      total,
      vatPercent: safeVatPercent,
      multipliers: {
        hours: safeHoursMultiplier,
        city: safeCityMultiplier,
        experience: safeExperienceMultiplier,
        equipment: safeEquipmentMultiplier,
        type: safeTypeMultiplier,
        shootingMethod: safeShootingMethodMultiplier,
        workload: safeWorkloadMultiplier
      },
      breakdown: [
        { label: 'السعر الأساسي', value: baseHourPrice, multiplier: 1, type: 'base' },
        { label: 'عدد الساعات', value: hours, multiplier: 1, type: 'hours' }
      ]
    };
  }

  // Calculate base price for one hour
  const basePrice = baseHourPrice;

  // Apply hours multiplier first, then all other multipliers (excluding workload_multiplier)
  const subtotal = basePrice * safeHoursMultiplier * safeCityMultiplier * safeExperienceMultiplier * safeEquipmentMultiplier * safeTypeMultiplier * safeShootingMethodMultiplier * safePlaceTypeMultiplier;

  // Calculate VAT
  const vatAmount = (subtotal * safeVatPercent) / 100;

  // Calculate total before workload multiplier
  const totalBeforeWorkload = subtotal + vatAmount;

  // Apply workload_multiplier ONLY at the final stage
  const total = totalBeforeWorkload * safeWorkloadMultiplier;

  // Ensure minimum price (at least base hour price)
  const finalTotal = Math.max(total, baseHourPrice);

  // Build breakdown array
  const breakdown = [
    {
      label: 'السعر الأساسي للساعة',
      value: baseHourPrice,
      multiplier: 1,
      type: 'base'
    },
  ];

  // Show hours multiplier only when it differs from 1 (i.e. when duration has a real effect)
  if (safeHoursMultiplier !== 1.0) {
    breakdown.push({
      label: 'مضاعف المدة',
      value: safeHoursMultiplier,
      multiplier: safeHoursMultiplier,
      type: 'hours'
    });
  }

  // Add multiplier breakdowns (only if not 1.0)
  if (safeCityMultiplier !== 1.0) {
    breakdown.push({
      label: 'مضاعف المدينة',
      value: safeCityMultiplier,
      multiplier: safeCityMultiplier,
      type: 'city'
    });
  }

  if (safeExperienceMultiplier !== 1.0) {
    breakdown.push({
      label: 'مضاعف الخبرة',
      value: safeExperienceMultiplier,
      multiplier: safeExperienceMultiplier,
      type: 'experience'
    });
  }

  if (safeEquipmentMultiplier !== 1.0) {
    breakdown.push({
      label: 'مضاعف المعدات',
      value: safeEquipmentMultiplier,
      multiplier: safeEquipmentMultiplier,
      type: 'equipment'
    });
  }

  if (safeTypeMultiplier !== 1.0) {
    breakdown.push({
      label: 'مضاعف نوع التصوير',
      value: safeTypeMultiplier,
      multiplier: safeTypeMultiplier,
      type: 'type'
    });
  }

  if (safeShootingMethodMultiplier !== 1.0) {
    breakdown.push({
      label: 'مضاعف وسيلة التصوير',
      value: safeShootingMethodMultiplier,
      multiplier: safeShootingMethodMultiplier,
      type: 'shootingMethod'
    });
  }

  if (safePlaceTypeMultiplier !== 1.0) {
    breakdown.push({
      label: 'مضاعف مكان التصوير',
      value: safePlaceTypeMultiplier,
      multiplier: safePlaceTypeMultiplier,
      type: 'placeType'
    });
  }

  return {
    baseHourPrice,
    hours,
    subtotal: Math.max(subtotal, baseHourPrice),
    vatAmount,
    total: finalTotal,
    vatPercent: safeVatPercent,
    multipliers: {
      hours: safeHoursMultiplier,
      city: safeCityMultiplier,
      experience: safeExperienceMultiplier,
      equipment: safeEquipmentMultiplier,
      type: safeTypeMultiplier,
      shootingMethod: safeShootingMethodMultiplier,
      placeType: safePlaceTypeMultiplier,
      workload: safeWorkloadMultiplier
    },
    breakdown
  };
}
